import { useMemo } from "react";

interface UseYTicksProps {
  max: number;
  maxTickCount?: number;
  min: number;
  minTickCount?: number;
  targetTickCount?: number;
}

const NICE_STEPS = [1, 2, 2.5, 5, 10];

const clampTickBounds = (
  minTickCount: number | undefined,
  maxTickCount: number | undefined,
): [number, number] => {
  const minTicks = Math.max(2, minTickCount ?? 4);
  const maxTicks = Math.max(minTicks, maxTickCount ?? 7);
  return [minTicks, maxTicks];
};

const buildTicks = (start: number, stop: number, step: number): number[] => {
  const ticks: number[] = [];
  for (let v = start; v <= stop + step * 1e-6; v += step) {
    const rounded = Math.abs(v) < 1e-12 ? 0 : Number(v.toFixed(12));
    ticks.push(rounded);
  }
  return ticks;
};

const computeStep = (roughStep: number): number => {
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalized = roughStep / magnitude;
  const chosen =
    NICE_STEPS.find(c => normalized <= c) ?? NICE_STEPS[NICE_STEPS.length - 1];
  return chosen * magnitude;
};

const alignTicksToStep = (
  domainMin: number,
  domainMax: number,
  step: number,
): number[] => {
  const start = Math.floor(domainMin / step) * step;
  const stop = Math.ceil(domainMax / step) * step;
  return buildTicks(start, stop, step);
};

const clampToMaxTicks = (
  domainMin: number,
  domainMax: number,
  step: number,
  maxTicks: number,
): { ticks: number[]; step: number } => {
  interface StepState {
    step: number;
    ticks: number[];
  }
  const initial: StepState = {
    step,
    ticks: alignTicksToStep(domainMin, domainMax, step),
  };

  const result = Array.from({ length: 64 }).reduce<StepState>(state => {
    if (state.ticks.length <= maxTicks) return state;
    const nextStep = state.step * 2;
    const nextTicks = alignTicksToStep(domainMin, domainMax, nextStep);
    return { step: nextStep, ticks: nextTicks };
  }, initial);

  return { ticks: result.ticks, step: result.step };
};

const expandToMinTicks = (
  domainMin: number,
  domainMax: number,
  step: number,
  minTicks: number,
  maxTicks: number,
): { ticks: number[]; step: number } => {
  interface StepState {
    step: number;
    ticks: number[];
  }
  const initial: StepState = {
    step,
    ticks: alignTicksToStep(domainMin, domainMax, step),
  };

  const result = [0, 1, 2].reduce<StepState>(state => {
    if (state.ticks.length >= minTicks) return state;
    const candidateStep = state.step / 2;
    const candidateTicks = alignTicksToStep(
      domainMin,
      domainMax,
      candidateStep,
    );
    if (candidateTicks.length > maxTicks) return state;
    return { step: candidateStep, ticks: candidateTicks };
  }, initial);

  return { ticks: result.ticks, step: result.step };
};

const generateCompactTicks = (
  domainMin: number,
  domainMax: number,
  desired: number,
  minTicks: number,
  maxTicks: number,
): number[] => {
  const range = domainMax - domainMin;
  const initialStep = computeStep(range / Math.max(desired - 1, 1));
  const { step: clampedStep } = clampToMaxTicks(
    domainMin,
    domainMax,
    initialStep,
    maxTicks,
  );
  const { ticks } = expandToMinTicks(
    domainMin,
    domainMax,
    clampedStep,
    minTicks,
    maxTicks,
  );
  return ticks;
};

const ensureNegativeTickWhenCrossZero = (
  ticks: number[],
  domainMin: number,
  domainMax: number,
  maxTicks: number,
): number[] => {
  if (domainMin >= 0 || domainMax <= 0) return ticks;
  const hasNegative = ticks.some(v => v < 0);
  if (hasNegative) return ticks;

  const rangeNeg = Math.abs(domainMin);
  const rough = rangeNeg / 2;
  const step = computeStep(rough);
  const candidate = -Math.min(step, rangeNeg);
  if (ticks.includes(candidate)) return ticks;

  const next = [...ticks, candidate].sort((a, b) => a - b);
  if (next.length <= maxTicks) return next;

  const last = next[next.length - 1];
  if (last > 0) next.pop();
  else next.shift();
  return next;
};

export const useYTicks = ({
  min,
  max,
  targetTickCount = 5,
  minTickCount,
  maxTickCount,
}: UseYTicksProps): number[] =>
  useMemo(() => {
    if (!Number.isFinite(min) || !Number.isFinite(max)) return [];

    const domainMin = Math.min(min, max);
    const domainMax = Math.max(min, max);

    if (domainMin === domainMax) {
      const base = Math.abs(domainMax) || 1;
      const step = Math.pow(10, Math.floor(Math.log10(base)));
      const center = domainMax;
      return [
        center - 2 * step,
        center - step,
        center,
        center + step,
        center + 2 * step,
      ];
    }

    const [minTicks, maxTicks] = clampTickBounds(minTickCount, maxTickCount);
    const desired = Math.max(targetTickCount, minTicks);

    let ticks = generateCompactTicks(
      domainMin,
      domainMax,
      desired,
      minTicks,
      maxTicks,
    );
    ticks = ensureNegativeTickWhenCrossZero(
      ticks,
      domainMin,
      domainMax,
      maxTicks,
    );
    return ticks;
  }, [min, max, targetTickCount, minTickCount, maxTickCount]);

export default useYTicks;
